import DashboardLayout from "@/components/DashboardLayout";
import ReportIssueButton from "@/components/errors/ReportIssueButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trpc } from "@/lib/trpc";
import { keepPreviousData } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Search, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "wouter";
import type {
  CustomerIntelligenceCategory,
  CustomerIntelligenceOrigin,
  CustomerIntelligenceRelationship,
} from "@shared/customerIntelligence";

const RELATIONSHIPS: Array<{ value: CustomerIntelligenceRelationship; label: string }> = [
  { value: "all", label: "Any relationship" },
  { value: "interested", label: "Interested" },
  { value: "bought", label: "Bought / sale confirmed" },
  { value: "owner", label: "Current owner" },
  { value: "interested_not_bought", label: "Interested — no confirmed sale" },
];

const ORIGINS: Array<{ value: CustomerIntelligenceOrigin; label: string }> = [
  { value: "all", label: "Any origin" },
  { value: "walk_in", label: "Walk-in" },
  { value: "website", label: "Website enquiry" },
  { value: "club_booking", label: "Club booking" },
  { value: "manual", label: "Manual entry" },
];

const CATEGORIES: Array<{ value: CustomerIntelligenceCategory; label: string }> = [
  { value: "all", label: "Any category" },
  { value: "home_automation", label: "Home automation" },
  { value: "two_channel", label: "Two-channel" },
  { value: "personal_audio", label: "Personal audio" },
  { value: "home_theatre", label: "Home theatre" },
  { value: "private_cinema", label: "Private cinema" },
];

function StatusPill({ children, tone }: { children: React.ReactNode; tone: "blue" | "green" | "purple" | "amber" }) {
  const classes = {
    blue: "bg-blue-50 text-blue-800 border-blue-200",
    green: "bg-emerald-50 text-emerald-800 border-emerald-200",
    purple: "bg-purple-50 text-purple-800 border-purple-200",
    amber: "bg-amber-50 text-amber-900 border-amber-200",
  }[tone];
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${classes}`}>{children}</span>;
}

export default function CustomerIntelligencePage() {
  const [query, setQuery] = useState("");
  const [relationship, setRelationship] = useState<CustomerIntelligenceRelationship>("all");
  const [origin, setOrigin] = useState<CustomerIntelligenceOrigin>("all");
  const [category, setCategory] = useState<CustomerIntelligenceCategory>("all");
  const enabled = query.trim().length >= 2 || origin !== "all" || category !== "all";
  const intelligence = trpc.customers.searchIntelligence.useQuery(
    { query: query.trim() || undefined, relationship, origin, category, limit: 100 },
    { enabled, placeholderData: keepPreviousData },
  );

  const counts = useMemo(() => {
    const rows = intelligence.data?.rows ?? [];
    return {
      interested: rows.filter((row) => row.interested).length,
      bought: rows.filter((row) => row.commerciallyConfirmed).length,
      owners: rows.filter((row) => row.currentOwner).length,
      notBought: rows.filter((row) => row.interestedNotBought).length,
    };
  }, [intelligence.data?.rows]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <Link href="/customers" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Customers
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">Customer Intelligence</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Find customers by brand, model, category, enquiry origin, commercial confirmation, or current ownership.
          </p>
        </div>

        <div className="rounded-lg border bg-card p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label="Search customer intelligence"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search a brand, model or product — e.g. T+A or Mark Levinson 5909"
              className="pl-9"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <select aria-label="Relationship" value={relationship} onChange={(event) => setRelationship(event.target.value as CustomerIntelligenceRelationship)} className="h-10 rounded-md border bg-background px-3 text-sm">
              {RELATIONSHIPS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <select aria-label="Origin" value={origin} onChange={(event) => setOrigin(event.target.value as CustomerIntelligenceOrigin)} className="h-10 rounded-md border bg-background px-3 text-sm">
              {ORIGINS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <select aria-label="Category" value={category} onChange={(event) => setCategory(event.target.value as CustomerIntelligenceCategory)} className="h-10 rounded-md border bg-background px-3 text-sm">
              {CATEGORIES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
          <p className="text-xs text-muted-foreground">
            “No confirmed sale” means no active invoice or received payment was found for the matching proposal. A missing DO alone never means no sale.
          </p>
        </div>

        {enabled && intelligence.data ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ["Interested", counts.interested],
              ["Sale confirmed", counts.bought],
              ["Current owner", counts.owners],
              ["No confirmed sale", counts.notBought],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border bg-card px-4 py-3">
                <div className="text-2xl font-semibold">{value}</div>
                <div className="text-xs text-muted-foreground">{label}</div>
              </div>
            ))}
          </div>
        ) : null}

        {!enabled ? (
          <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
            <Search className="mx-auto mb-3 h-9 w-9 opacity-30" />
            <p className="font-medium">Search by product, brand, category, or enquiry origin</p>
            <p className="mt-1 text-sm">Results always include links to the underlying evidence.</p>
          </div>
        ) : intelligence.isError && !intelligence.data ? (
          <div role="alert" className="rounded-lg border border-destructive/30 p-6 text-center">
            <p className="font-medium">Customer intelligence search could not be completed.</p>
            <div className="mt-3 flex justify-center gap-2">
              <Button variant="outline" size="sm" onClick={() => intelligence.refetch()}>Retry</Button>
              <ReportIssueButton surface="sales.customer_search" safeErrorCode={intelligence.error?.data?.code ?? "INTERNAL_SERVER_ERROR"} priorDataRetained={false} />
            </div>
          </div>
        ) : intelligence.isLoading && !intelligence.data ? (
          <div className="space-y-3">{Array.from({ length: 5 }, (_, index) => <div key={index} className="h-36 animate-pulse rounded-lg bg-muted" />)}</div>
        ) : intelligence.data?.rows.length ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{intelligence.data.total} matching customer{intelligence.data.total === 1 ? "" : "s"}</p>
            {intelligence.data.rows.map((row) => (
              <article key={row.customerId} className="rounded-lg border bg-card p-4" data-testid="customer-intelligence-result">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <Link href={`/customers/id/${row.customerId}`} className="font-semibold hover:underline">{row.customerName}</Link>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {row.salespersonName ? `PIC: ${row.salespersonName}` : "No PIC"}
                      {row.origins.length ? ` · ${row.origins.join(", ")}` : ""}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {row.interested ? <StatusPill tone="blue">Interested</StatusPill> : null}
                    {row.commerciallyConfirmed ? <StatusPill tone="green">Sale confirmed</StatusPill> : null}
                    {row.paymentReceived ? <StatusPill tone="green">Payment received</StatusPill> : null}
                    {row.currentOwner ? <StatusPill tone="purple">Current owner</StatusPill> : null}
                    {row.interestedNotBought ? <StatusPill tone="amber">No confirmed sale</StatusPill> : null}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  {row.matchedBrands.length ? <span><span className="text-muted-foreground">Brands:</span> {row.matchedBrands.join(", ")}</span> : null}
                  {row.matchedModels.length ? <span><span className="text-muted-foreground">Models:</span> {row.matchedModels.slice(0, 6).join(", ")}</span> : null}
                  {row.matchedCategories.length ? <span><span className="text-muted-foreground">Categories:</span> {row.matchedCategories.slice(0, 4).join(", ")}</span> : null}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Evidence</span>
                  {row.evidence.map((evidence) => (
                    <Link key={evidence.key} href={evidence.href} className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs hover:bg-accent">
                      {evidence.label}<ExternalLink className="h-3 w-3" />
                    </Link>
                  ))}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
            <Users className="mx-auto mb-3 h-9 w-9 opacity-30" />
            <p className="font-medium">No matching customers</p>
            <p className="mt-1 text-sm">Try a broader product term or remove a filter.</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
